import React, { useRef, useState, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
    intl,
    DmsAltBox,
    DmsPage,
    DmsTable,
    DmsButton,
    fetchData,
} from "dmsbasereact";
import Swal from "sweetalert2";
import { Typography, IconButton, Tooltip, Box } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { formatCnpj } from "../../utils/formatters";
import { APP_ROUTES } from "../../utils/NamedRoutes";
import { PartnerBranchLinkModal } from "../../components/PartnerBranchLinkModal";
import { getInstitutionCategoryOptions, getInstitutionCategoryLabel } from "../../constants/InstitutionType";

export const PartnerBranchDetailPage = () => {
    const table = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { matrizPk } = useParams();

    const [modalOpen, setModalOpen] = useState(false);

    const matrizName = location.state?.matrizName || intl.get("Institution").d("Institution");
    const matrizCnpj = location.state?.matrizCnpj || "";
    const matrizIsPublic = location.state?.matrizIsPublic || false;

    const handleGoBack = () => {
        navigate(APP_ROUTES.PartnerBranch);
    };

    const handleOpenModal = useCallback(() => {
        setModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setModalOpen(false);
    }, []);

    const handleSave = useCallback(() => {
        if (table.current) {
            table.current.updateTable();
        }
    }, []);

    const handleUnlink = useCallback(async (row) => {
        const result = await Swal.fire({
            icon: "warning",
            title: intl.get("ConfirmUnlinkBranch").d("Are you sure you want to unlink this branch?"),
            showCancelButton: true,
            confirmButtonText: intl.get("Yes").d("Yes"),
            cancelButtonText: intl.get("No").d("No"),
        });

        if (result.isConfirmed) {
            try {
                await fetchData(
                    `/partner/branch/link/${matrizPk}/${row.organization_branch_pk}`,
                    "DELETE",
                    true
                );
                Swal.fire({
                    icon: "success",
                    title: intl.get("BranchUnlinkedSuccessfully").d("Branch unlinked successfully"),
                });
                if (table.current) {
                    table.current.updateTable();
                }
            } catch (error) {
                Swal.fire({
                    icon: "error",
                    title: intl.get("FailedToUnlinkBranch").d("Failed to unlink branch"),
                });
            }
        }
    }, [matrizPk]);

    const header = [
        {
            label: intl.get("InstitutionName").d("Institution Name"),
            column_name: "name",
            filter: true,
        },
        {
            label: intl.get("CNPJ").d("CNPJ"),
            column_name: "cnpj",
            filter: true,
            sortable: true,
            properties: { filter: { field: "cnpj" } },
            function: (value) => formatCnpj(value),
        },
        {
            label: intl.get("Category").d("Category"),
            column_name: "category",
            filter: true,
            control: "select",
            options: getInstitutionCategoryOptions().map((opt) => ({
                label: opt.label,
                value: opt.value,
            })),
            sortable: false,
            function: (value) => getInstitutionCategoryLabel(value) || "-",
        },
        {
            label: intl.get("Address").d("Address"),
            column_name: "organization_branch",
            sortable: false,
            function: (value) => {
                const addr = value?.address;
                if (!addr) return "-";
                return [addr.address, addr.number].filter(Boolean).join(", ");
            },
        },
        {
            label: intl.get("ZipCode").d("Zip Code"),
            column_name: "organization_branch",
            sortable: false,
            function: (value) => value?.address?.zip_code || "-",
        },
        {
            label: intl.get("City").d("City"),
            column_name: "organization_branch",
            sortable: false,
            function: (value) => value?.address?.city || "-",
        },
        {
            label: intl.get("State").d("State"),
            column_name: "organization_branch",
            sortable: false,
            width: "80px",
            function: (value) => value?.address?.state || "-",
        },
        {
            label: intl.get("Actions").d("Actions"),
            column_name: "actions",
            sortable: false,
            width: "80px",
            element: (row) => (
                <Tooltip title={intl.get("Unlink").d("Unlink")}>
                    <IconButton
                        size="small"
                        onClick={() => handleUnlink(row)}
                        color="error"
                    >
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    return (
        <DmsPage name="PartnerBranchDetail">
            <DmsAltBox.Card>
                <DmsAltBox.Header>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Tooltip title={intl.get("Back").d("Back")}>
                            <IconButton
                                size="small"
                                onClick={handleGoBack}
                                sx={{
                                    color: "primary.main",
                                    backgroundColor: "#fff",
                                    "&:hover": { backgroundColor: "grey.100" },
                                }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                        </Tooltip>
                        <Box>
                            <Typography variant="h3" sx={{ fontSize: "1.1rem" }}>
                                {matrizName}
                            </Typography>
                            {matrizCnpj && (
                                <Typography variant="caption" color="text.secondary">
                                    {formatCnpj(matrizCnpj)}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </DmsAltBox.Header>
                <DmsAltBox.Body>
                    <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
                        <DmsButton
                            icon={<AddIcon />}
                            text={intl.get("AddEconomicGroupOrBranch").d("Add Economic Group or Branch")}
                            onClick={handleOpenModal}
                            variant="primary"
                        />
                    </Box>
                    <DmsTable
                        header={header}
                        managed={true}
                        needs_authentication={true}
                        ref={table}
                        url={`/partner/branch/${matrizPk}/branches`}
                        export_table={true}
                    />
                </DmsAltBox.Body>
            </DmsAltBox.Card>

            <PartnerBranchLinkModal
                isVisible={modalOpen}
                onClose={handleCloseModal}
                onSave={handleSave}
                fixedMatrizPk={Number(matrizPk)}
                fixedMatrizCnpj={matrizCnpj}
                fixedMatrizName={matrizName}
                fixedIsPublic={matrizIsPublic}
            />
        </DmsPage>
    );
};

export default PartnerBranchDetailPage;
